import BoltRounded from '@mui/icons-material/BoltRounded';
import SummarizeOutlined from '@mui/icons-material/SummarizeOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { inferOutput } from '@trpc/tanstack-react-query';
import type { ReactNode } from 'react';
import {
  deltaPercentPoints,
  formatCompactCurrency,
  formatPercent,
  formatWhole,
} from '../../../lib/format';
import type { useTRPC } from '../../../lib/trpc';
import { ACTION_TIP, ACTION_TONE, SAVINGS_TIP, UPLIFT_TIP } from '../data/constants';
import type { MediaKpi } from '../data/types';
import { formatKpiValue } from '../data/utils';
import { InfoTip } from '../shared/InfoTip';

type TRPC = ReturnType<typeof useTRPC>;
export type ExecutiveSummaryData = inferOutput<TRPC['mediaPerformance']['executiveSummary']>;

interface DeltaChipProps {
  points: number | null;
  invert?: boolean;
}

interface StatLineProps {
  label: string;
  value: string;
  points?: number | null;
  invert?: boolean;
  info?: string;
}

interface SectionLabelProps {
  children: ReactNode;
}

interface ExecutiveSummaryProps {
  data: ExecutiveSummaryData;
  kpi: MediaKpi;
}

function DeltaChip({ points, invert = false }: DeltaChipProps): ReactNode {
  const signed = points === null ? null : invert ? -points : points;
  const good = signed !== null && signed >= 0;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        justifyContent: 'center',
        minWidth: 44,
        fontSize: 10,
        fontWeight: 700,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        whiteSpace: 'nowrap',
        color: signed === null ? 'text.secondary' : good ? 'success.main' : 'error.dark',
        bgcolor: signed === null ? 'background.muted' : good ? 'score.high.bg' : 'score.low.bg',
      }}
    >
      {signed === null ? '-' : `${signed > 0 ? '+' : ''}${signed}%`}
    </Box>
  );
}

function StatLine({ label, value, points, invert = false, info }: StatLineProps): ReactNode {
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1 }}
    >
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {info ? <InfoTip text={info} /> : null}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {value}
        </Box>
        {points !== undefined ? <DeltaChip points={points} invert={invert} /> : null}
      </Box>
    </Box>
  );
}

function SectionLabel({ children }: SectionLabelProps): ReactNode {
  return (
    <Box
      component="span"
      sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', display: 'block', mb: 1 }}
    >
      {children}
    </Box>
  );
}

export function ExecutiveSummary({ data, kpi }: ExecutiveSummaryProps): ReactNode {
  const symbol = data.currencySymbol;
  const action = ACTION_TONE[data.actionLevel];

  return (
    <Box
      sx={(theme) => ({
        p: 4,
        mb: 4,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: theme.palette.border.default,
        borderRadius: `${theme.radius.xl}px`,
        boxShadow: theme.shadows[5],
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SummarizeOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="h4">Executive Summary</Typography>
      </Box>

      <Box
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
          pt: 2,
          '@media print': { gridTemplateColumns: '1fr 1fr 1fr' },
        })}
      >
        <Box
          sx={(theme) => ({
            pr: { md: 4 },
            borderRight: { md: `1px solid ${theme.palette.divider}` },
            '@media print': {
              pr: 4,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          })}
        >
          <SectionLabel>Portfolio Overview</SectionLabel>
          <StatLine
            label="Active Media (assets)"
            value={formatWhole(data.activeCreatives.value)}
            points={deltaPercentPoints(data.activeCreatives.delta)}
          />
          <StatLine
            label="Total Spend"
            value={formatCompactCurrency(data.totalSpend.value, symbol)}
            points={deltaPercentPoints(data.totalSpend.delta)}
          />
          <StatLine
            label={`Avg. Performance · ${kpi.label}`}
            value={formatKpiValue(data.avgKpi.value, kpi, symbol)}
            points={deltaPercentPoints(data.avgKpi.delta)}
            invert={!kpi.higherIsBetter}
          />
        </Box>

        <Box
          sx={(theme) => ({
            px: { md: 4 },
            borderRight: { md: `1px solid ${theme.palette.divider}` },
            '@media print': {
              px: 4,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          })}
        >
          <SectionLabel>Media Health</SectionLabel>
          <StatLine
            label="Over Performing"
            value={formatWhole(data.over.value)}
            points={deltaPercentPoints(data.over.delta)}
          />
          <StatLine
            label="Under Performing"
            value={formatWhole(data.under.value)}
            points={deltaPercentPoints(data.under.delta)}
            invert
          />
          <StatLine
            label="Fatigued"
            value={formatWhole(data.fatigued.value)}
            points={deltaPercentPoints(data.fatigued.delta)}
            invert
          />
        </Box>

        <Box sx={{ pl: { md: 4 }, '@media print': { pl: 4 } }}>
          <SectionLabel>Top Opportunity</SectionLabel>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              py: 1,
            }}
          >
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}
            >
              Action Required
              <InfoTip text={ACTION_TIP} />
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 14,
                fontWeight: 700,
                px: 2,
                py: 1,
                borderRadius: 1,
                textTransform: 'capitalize',
                color: action.fg,
                bgcolor: action.bg,
              }}
            >
              {data.actionLevel}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              py: 1,
              px: 1,
              mx: -1,
              borderRadius: 2,
              background: (theme) =>
                `linear-gradient(90deg, ${theme.palette.primary.bg}, transparent)`,
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
              <BoltRounded sx={{ fontSize: 14 }} />
              Potential Savings
              <InfoTip text={SAVINGS_TIP} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Box
                component="span"
                sx={(theme) => ({
                  fontSize: 16,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  ...theme.mixins.gradientText,
                  '@media print': {
                    background: 'none',
                    color: 'primary.main',
                    WebkitTextFillColor: 'currentColor',
                  },
                })}
              >
                {formatCompactCurrency(data.potentialSavings.spend, symbol)}
              </Box>
              <Box component="span" sx={{ fontSize: 10, fontWeight: 600, color: 'text.disabled' }}>
                {formatPercent(data.potentialSavings.spendPct)} of spend
              </Box>
            </Box>
          </Box>
          <StatLine
            label="Potential Uplift"
            value={formatCompactCurrency(data.potentialUplift?.spend ?? null, symbol, true)}
            info={UPLIFT_TIP}
          />
        </Box>
      </Box>
    </Box>
  );
}
